/**
 * YADA Products & Services — official offerings.
 * i18n keys resolved at runtime via t().
 *
 * Three flagship EdTech products (system category) lead the catalogue,
 * mirroring the organisation's EdTech product line. Services and tools
 * cover event-based and physical-channel offerings.
 */

// Hero image lives in public/ (served at the URL literal below) so the
// prerender pass (scripts/prerender.tsx) can import this module without
// hitting Node's bare-path .png resolution. The src/assets/ copy still
// exists; Vite still copies it to dist/assets/ but we ignore the hashed
// version in favour of the public/ literal for SSR + CSR parity.
// @see src/components/ui/Logo.tsx — same pattern for the brand mark
const valuesIncubatorHero = '/student-campus.png'

export interface ServiceArea {
  id: string
  icon: string // iconify icon name
  titleKey: string
  descriptionKey: string
}

export interface ProductItem {
  id: string
  icon: string // iconify icon name
  category: 'system' | 'service' | 'tool'
  titleKey: string
  descriptionKey: string
  featuresKey?: string // i18n key returning string[] via { returnObjects: true }
  badgeKey?: string
  accentGradient: string
  ctaLink: string
  // ── Comparison-table fields (system products) ─────────────
  // Rendered by the 產品比較總表 on /products, which filters
  // `category === 'system'`. All optional, so a future product can
  // be added with a short name + audience + feature list and get a
  // table row for free — no component edit.
  //
  // `audienceKey` deliberately carries NO "適用對象：" prefix; that
  // label is the table's column header (`tableHeaders.audience`).
  //
  // `tableFeaturesKey` is named for the table, NOT `coreFeaturesKey`:
  // `products.offers.valuesIncubator.coreFeatures` already exists as
  // a {title,text}[] rendered by ValuesIncubatorPage, and the two
  // shapes are incompatible.
  //
  // Note: `tableFeaturesKey` is a single unbounded string. If a future
  // product needs 8+ features the cell gets very tall and the fix is a
  // shape change (string[] → chips) — which would be a breaking change
  // to this field's contract, not a value edit.
  audienceKey?: string // one short cell
  tableFeaturesKey?: string // comma-separated short feature list
  shortTitleKey?: string // short name for dense layouts; falls back to titleKey
  // Rich content fields (system products)
  imageSrc?: string // imported PNG path
  imageWidth?: number // CLS-safe intrinsic width
  imageHeight?: number
  imageAltKey?: string // i18n alt text
  detailRoute?: string // deep-link to /products/<id>
}

export const serviceAreas: ServiceArea[] = [
  {
    id: 'directSupport',
    icon: 'lucide:hand-heart',
    titleKey: 'products.areas.items.directSupport.title',
    descriptionKey: 'products.areas.items.directSupport.text',
  },
  {
    id: 'techSolutions',
    icon: 'lucide:workflow',
    titleKey: 'products.areas.items.techSolutions.title',
    descriptionKey: 'products.areas.items.techSolutions.text',
  },
  {
    id: 'research',
    icon: 'lucide:flask-conical',
    titleKey: 'products.areas.items.research.title',
    descriptionKey: 'products.areas.items.research.text',
  },
  {
    id: 'advocacy',
    icon: 'lucide:megaphone',
    titleKey: 'products.areas.items.advocacy.title',
    descriptionKey: 'products.areas.items.advocacy.text',
  },
]

export const products: ProductItem[] = [
  // === Flagship EdTech (system) ===
  {
    id: 'valuesIncubator',
    icon: 'lucide:sparkles',
    category: 'system',
    titleKey: 'products.offers.valuesIncubator.title',
    descriptionKey: 'products.offers.valuesIncubator.tagline',
    featuresKey: 'products.offers.valuesIncubator.highlights',
    badgeKey: 'products.offers.valuesIncubator.badge',
    accentGradient: 'from-primary to-primary-dark',
    ctaLink: '/products/values-incubator',
    imageSrc: valuesIncubatorHero,
    imageWidth: 763,
    imageHeight: 849,
    imageAltKey: 'products.offers.valuesIncubator.imageAlt',
    detailRoute: '/products/values-incubator',
    shortTitleKey: 'products.offers.valuesIncubator.shortTitle',
    audienceKey: 'products.offers.valuesIncubator.audience',
    tableFeaturesKey: 'products.offers.valuesIncubator.tableFeatures',
  },
  {
    id: 'aiAdvisor',
    icon: 'lucide:message-circle',
    category: 'system',
    titleKey: 'products.offers.aiAdvisor.title',
    descriptionKey: 'products.offers.aiAdvisor.tagline',
    featuresKey: 'products.offers.aiAdvisor.features',
    accentGradient: 'from-yada-gold to-yada-olive',
    ctaLink: 'mailto:info@yada.hk',
    shortTitleKey: 'products.offers.aiAdvisor.shortTitle',
    audienceKey: 'products.offers.aiAdvisor.audience',
    tableFeaturesKey: 'products.offers.aiAdvisor.tableFeatures',
  },
  {
    id: 'biteLearn',
    icon: 'lucide:graduation-cap',
    category: 'system',
    titleKey: 'products.offers.biteLearn.title',
    descriptionKey: 'products.offers.biteLearn.tagline',
    featuresKey: 'products.offers.biteLearn.features',
    accentGradient: 'from-yada-green to-yada-green-dark',
    ctaLink: 'mailto:info@yada.hk',
    shortTitleKey: 'products.offers.biteLearn.shortTitle',
    audienceKey: 'products.offers.biteLearn.audience',
    tableFeaturesKey: 'products.offers.biteLearn.tableFeatures',
  },
  {
    id: 'nationalEducation',
    icon: 'lucide:landmark',
    category: 'system',
    titleKey: 'products.offers.nationalEducation.title',
    descriptionKey: 'products.offers.nationalEducation.tagline',
    featuresKey: 'products.offers.nationalEducation.features',
    accentGradient: 'from-[#A7C957] to-[#5E8C3E]',
    ctaLink: 'mailto:info@yada.hk',
    shortTitleKey: 'products.offers.nationalEducation.shortTitle',
    audienceKey: 'products.offers.nationalEducation.audience',
    tableFeaturesKey: 'products.offers.nationalEducation.tableFeatures',
  },
  {
    // CSP — 升學及職涯起航平台. The product has its OWN website
    // (https://csp.yada.hk), so `ctaLink` points off-site while
    // `detailRoute` points at its on-site page (/products/csp), which the
    // card title links to. See PRODUCT_EXTERNAL_CTA in
    // src/content/productRoutes.ts — the catalogue card and the homepage
    // grid read the external CTA from there.
    id: 'csp',
    icon: 'lucide:rocket',
    category: 'system',
    titleKey: 'products.offers.csp.title',
    descriptionKey: 'products.offers.csp.tagline',
    // The sibling system products point their featuresKey at the same
    // {title,text}[] shape (`products.offers.aiAdvisor.features`), so the
    // CSP key points at the same shape rather than at a plain string.
    featuresKey: 'products.offers.csp.features',
    badgeKey: 'products.offers.csp.badge',
    // Green → deep green, matching the draft card's own band colour
    // (#3F6B2C) rather than the site's olive. Must match the
    // `accent_gradient` the CMS seed script writes for the csp row.
    accentGradient: 'from-yada-green to-yada-green-dark',
    ctaLink: 'https://csp.yada.hk',
    detailRoute: '/products/csp',
    shortTitleKey: 'products.offers.csp.shortTitle',
    audienceKey: 'products.offers.csp.audience',
    tableFeaturesKey: 'products.offers.csp.tableFeatures',
  },
  // === Service line items ===
  {
    id: 'hackathon',
    icon: 'lucide:trophy',
    category: 'service',
    titleKey: 'products.offers.services.items.hackathon.title',
    descriptionKey: 'products.offers.services.items.hackathon.text',
    accentGradient: 'from-yada-gold to-yada-olive',
    ctaLink: 'mailto:info@yada.hk',
  },
  {
    id: 'explorationTrip',
    icon: 'lucide:map',
    category: 'service',
    titleKey: 'products.offers.services.items.explorationTrip.title',
    descriptionKey: 'products.offers.services.items.explorationTrip.text',
    accentGradient: 'from-yada-green to-yada-green-dark',
    ctaLink: 'mailto:info@yada.hk',
  },
  {
    id: 'mentorship',
    icon: 'lucide:users',
    category: 'service',
    titleKey: 'products.offers.services.items.mentorship.title',
    descriptionKey: 'products.offers.services.items.mentorship.text',
    accentGradient: 'from-yada-olive to-yada-brown',
    ctaLink: 'mailto:info@yada.hk',
  },
  // === Tools ===
  {
    id: 'flashCards',
    icon: 'lucide:layout-grid',
    category: 'tool',
    titleKey: 'products.offers.tools.items.flashCards.title',
    descriptionKey: 'products.offers.tools.items.flashCards.text',
    accentGradient: 'from-yada-brown to-yada-coffee',
    ctaLink: 'mailto:info@yada.hk',
  },
  {
    id: 'clawMachine',
    icon: 'lucide:gamepad-2',
    category: 'tool',
    titleKey: 'products.offers.tools.items.clawMachine.title',
    descriptionKey: 'products.offers.tools.items.clawMachine.text',
    accentGradient: 'from-yada-coffee to-yada-olive',
    ctaLink: 'mailto:info@yada.hk',
  },
]
